import React, { useState } from 'react';
import {
    TextField,
    Button,
    Container,
    Box,
    Typography,
    Alert,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useHLogin } from '../items/hooks/HLogin';
import { useHRegistration } from '../items/hooks/HRegistration';
import RegisterForm from '../dialogs/RegisterForm';
import { API_BASE } from '../../config/constants';
import '../../App.scss';

interface LoginPageProps {
    onLoginSuccess: (user: { email: string; [key: string]: any }) => void;
}

const G20GradientVideo = '/assets/video3.mp4';

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
    const [registerOpen, setRegisterOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetError, setResetError] = useState<string | null>(null);
    const [resetSuccess, setResetSuccess] = useState<string | null>(null);
    const [registerData, setRegisterData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });

    // 2FA states
    const [requires2FA, setRequires2FA] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [loginToken, setLoginToken] = useState<string | null>(null);

    const theme = (localStorage.getItem('theme') as 'light' | 'dark') || 'light';

    // Apply theme class to body so MUI dialogs (portaled outside root) inherit it
    React.useEffect(() => {
        document.body.classList.remove('theme-light', 'theme-dark');
        document.body.classList.add(`theme-${theme}`);
    }, [theme]);

    const {
        email,
        setEmail,
        password,
        setPassword,
        rememberMe,
        setRememberMe,
        loading: loginLoading,
        error: loginError,
        success: loginSuccess,
        setError: setLoginError,
        setSuccess: setLoginSuccess,
        login,
        verify2FA,
    } = useHLogin();

    const {
        loading: registrationLoading,
        error: registrationError,
        success: registrationSuccess,
        setError: setRegistrationError,
        setSuccess: setRegistrationSuccess,
        registerUser,
    } = useHRegistration();

    const loading = loginLoading || registrationLoading;
    const error = registrationError || loginError;
    const success = registrationSuccess || loginSuccess;

    const openResetDialog = () => {
        setResetEmail(email.trim());
        setResetError(null);
        setResetSuccess(null);
        setResetOpen(true);
    };

    const handleResetRequest = async (e: React.FormEvent) => {
        e.preventDefault();

        const normalizedEmail = resetEmail.trim().toLowerCase();
        console.log('[LoginPage] Reset request for email:', normalizedEmail);
        if (!normalizedEmail) {
            setResetError('Email is required.');
            return;
        }

        setResetLoading(true);
        setResetError(null);
        setResetSuccess(null);

        try {
            console.log('[LoginPage] Sending password reset request to backend');
            const response = await fetch(`${API_BASE}/api/users/auth/password/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: normalizedEmail }),
            });

            const payload = await response.json().catch(() => ({}));
            console.log('[LoginPage] Reset request response:', { status: response.ok, success: payload?.success });

            if (!response.ok || payload?.success === false) {
                throw new Error(payload?.message || 'Failed to request password reset.');
            }

            setResetSuccess(payload?.message || 'If that email exists, a reset link has been sent.');
            console.log('[LoginPage] Reset request successful');
        } catch (err: any) {
            console.error('[LoginPage] Reset request error:', err?.message);
            setResetError(err?.message || 'Failed to request password reset.');
        } finally {
            setResetLoading(false);
        }
    };

const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If 2FA is required and we have a code, verify it
    if (requires2FA && loginToken) {
        const result = await verify2FA(loginToken, otpCode);
        if (result.success && result.user) {
            const loggedInUser = {
                email,
                name: `${result.user.first_name || ''} ${result.user.last_name || ''}`.trim() || email,
                ...result.user
            };
            onLoginSuccess(loggedInUser);
        }
        return;
    }
    
    // Standard login flow
    const result = await login({ email, password, rememberMe });
    
    // Check if 2FA is required
    if ((result as any).requires2fa && (result as any).loginToken) {
        setRequires2FA(true);
        setLoginToken((result as any).loginToken);
        setOtpCode('');
        setLoginError(null);
    } else if (result.success && result.user) {
        const loggedInUser = {
            email,
            name: `${result.user.first_name || ''} ${result.user.last_name || ''}`.trim() || email,
            ...result.user
        };
        onLoginSuccess(loggedInUser);
    }
};

    const handleRegister = async () => {
        console.log('[LoginPage] handleRegister called', {
            email: registerData.email,
            hasFirstName: Boolean(registerData.firstName),
            hasLastName: Boolean(registerData.lastName),
            passwordLength: registerData.password.length,
            confirmPasswordLength: registerData.confirmPassword.length,
        });

        setLoginError(null);
        setLoginSuccess(null);
        setRegistrationError(null);
        setRegistrationSuccess(null);

        // Validation
        if (!registerData.email || !registerData.password || !registerData.firstName || !registerData.lastName) {
            console.warn('[LoginPage] register validation failed: missing required fields');
            setRegistrationError('All fields are required.');
            return;
        }

        if (registerData.password !== registerData.confirmPassword) {
            console.warn('[LoginPage] register validation failed: password mismatch');
            setRegistrationError('Passwords do not match.');
            return;
        }

        if (registerData.password.length < 6) {
            console.warn('[LoginPage] register validation failed: password too short');
            setRegistrationError('Password must be at least 6 characters long.');
            return;
        }

        const result = await registerUser({
            firstName: registerData.firstName,
            lastName: registerData.lastName,
            email: registerData.email,
            password: registerData.password,
        });

        console.log('[LoginPage] register-user result', result);

            if (result.success) {
                // Keep the register dialog open and preserve the entered data so the
                // user can see the confirmation message and close the dialog manually.
                setRegistrationSuccess(result.message || 'Registration successful.');
            } else {
                setRegistrationError(result.message || 'Registration failed.');
            }
    };

    return (
        <div className={`App login-page-shell theme-${theme}`}>
            <video className='header-bar-bg-video' autoPlay loop muted playsInline src={G20GradientVideo} />
            <div className='login-page-overlay' />

            <Container maxWidth='sm' className='login-page-content'>
                <Box
                    className='login-card'
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 4,
                        borderRadius: 2,
                        boxShadow: 3,
                    }}
                >
                    <Typography component='h1' variant='h4' sx={{ marginBottom: 3, fontWeight: 600 }}>
                        XM Login
                    </Typography>

                    {error && (
                        <Alert severity='error' sx={{ width: '100%', marginBottom: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity='success' sx={{ width: '100%', marginBottom: 2 }}>
                            {success}
                        </Alert>
                    )}

                    <Box component='form' onSubmit={handleLogin} sx={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label='Email'
                            type='email'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            margin='normal'
                            autoComplete='username'
                            required
                            disabled={loading}
                            autoFocus
                        />

                        <TextField
                            fullWidth
                            label='Password'
                            type='password'
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            margin='normal'
                            autoComplete='current-password'
                            required
                            disabled={loading}
                        />

                        {requires2FA && (
                            <TextField
                                fullWidth
                                label='2FA Code'
                                placeholder='Enter 6-digit code from your authenticator app'
                                value={otpCode}
                                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                margin='normal'
                                inputProps={{ maxLength: 6 }}
                                disabled={loading}
                                helperText='Enter the 6-digit code from Google Authenticator, 1Password, or Authy'
                            />
                        )}

                        {!requires2FA && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label='Remember me'
                                sx={{ marginTop: 1 }}
                            />
                        )}

                        <Button
                            fullWidth
                            variant='contained'
                            color='primary'
                            sx={{ marginTop: 3, padding: '10px 0', fontSize: '16px' }}
                            type='submit'
                            disabled={loading || (requires2FA && otpCode.length !== 6)}
                        >
                            {loading ? (requires2FA ? 'Verifying code...' : 'Logging in...') : (requires2FA ? 'Verify 2FA' : 'Login')}
                        </Button>

                        {requires2FA && (
                            <Button
                                fullWidth
                                variant='outlined'
                                color='primary'
                                sx={{ marginTop: 2, padding: '10px 0', fontSize: '16px' }}
                                onClick={() => {
                                    setRequires2FA(false);
                                    setLoginToken(null);
                                    setOtpCode('');
                                    setLoginError(null);
                                }}
                                disabled={loading}
                            >
                                Back to Login
                            </Button>
                        )}

                        <Button
                            fullWidth
                            variant='outlined'
                            color='primary'
                            sx={{ marginTop: 2, padding: '10px 0', fontSize: '16px' }}
                            onClick={() => {
                                setRegisterOpen(true);
                            }}
                            disabled={loading}
                        >
                            Register New User
                        </Button>

                        <button
                            type='button'
                            className='login-secondary-link'
                            onClick={openResetDialog}
                            disabled={loading}
                        >
                            Reset password
                        </button>
                    </Box>
                </Box>
            </Container>

            <RegisterForm
                open={registerOpen}
                loading={loading}
                registerData={registerData}
                setRegisterData={setRegisterData}
                onClose={() => setRegisterOpen(false)}
                onSubmit={handleRegister}
                error={registrationError}
            />

            <Dialog
                open={resetOpen}
                onClose={() => {
                    if (!resetLoading) setResetOpen(false);
                }}
                fullWidth
                maxWidth='xs'
            >
                <Box component='form' onSubmit={handleResetRequest}>
                    <DialogTitle>Reset password</DialogTitle>
                    <DialogContent>
                        <Typography className='login-dialog-copy'>
                            Confirm the email address for the account you want to reset. We will send a one-time link if the account exists.
                        </Typography>

                        {resetError && (
                            <Alert severity='error' sx={{ mt: 2 }}>
                                {resetError}
                            </Alert>
                        )}

                        {resetSuccess && (
                            <Alert severity='success' sx={{ mt: 2 }}>
                                {resetSuccess}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            margin='normal'
                            label='Email'
                            type='email'
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            autoComplete='email'
                            disabled={resetLoading || Boolean(resetSuccess)}
                            required
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setResetOpen(false)} disabled={resetLoading}>
                            Cancel
                        </Button>
                        <Button type='submit' variant='contained' disabled={resetLoading || Boolean(resetSuccess)}>
                            {resetLoading ? 'Sending...' : 'Send reset link'}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </div>
    );
};

export default LoginPage;
